// @file Handles update_post and update_draft URL query param shortcuts for Surface
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { getAndClearSearchParam } from '@@/bits/location'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { nextTick, watch } from 'vue'

function useSurfaceUrlShortcuts(): void {
  const rawUpdatePostHashid = getAndClearSearchParam('update_post')
  const rawDraftId = getAndClearSearchParam('update_draft')

  // update_post takes priority; both params are cleared on read so neither leaks into the URL.
  if (rawUpdatePostHashid != null) {
    openPostEditor(rawUpdatePostHashid)
    return
  }

  if (rawDraftId != null) {
    openDraftComposer(rawDraftId)
  }
}

async function expandInModal(cid: string): Promise<void> {
  // Wait for Vue to flush updates so the composer modal list registers the
  // entity before we imperatively expand it.
  await nextTick()
  const postComposerModalStore = usePostComposerModalStore()
  const { isDockableModalListRegistered } = storeToRefs(postComposerModalStore)
  if (isDockableModalListRegistered.value) {
    postComposerModalStore.expandComposerModal(cid)
    return
  }
  // DockableModalList hasn't mounted yet — watch until it does.
  const unwatch = watch(isDockableModalListRegistered, (registered) => {
    if (!registered) return
    unwatch()
    postComposerModalStore.expandComposerModal(cid)
  })
}

function openWhenReady(hasFetched: Ref<boolean>, open: () => Promise<void>): void {
  if (hasFetched.value) {
    void open()
    return
  }
  const unwatch = watch(hasFetched, async (fetched) => {
    if (!fetched) return
    unwatch()
    // If the user opened a composer while waiting for data, skip the URL shortcut.
    if (usePostComposerModalStore().hasActiveComposerModal) return
    await open()
  })
}

function openPostEditor(rawHashid: string): void {
  const postHashid = removeHashidPrefix(rawHashid)
  const surfacePostsStore = useSurfacePostsStore()
  const { hasFetchedPosts, postEntitiesByHashId } = storeToRefs(surfacePostsStore)

  async function openPost(): Promise<void> {
    const post = postEntitiesByHashId.value[postHashid]
    if (post == null) return
    surfacePostsStore.startEditingPost({ postCid: post.cid })
    await expandInModal(post.cid)
  }

  openWhenReady(hasFetchedPosts, openPost)
}

function openDraftComposer(rawDraftId: string): void {
  const draftId = parseInt(rawDraftId, 10)
  if (isNaN(draftId)) return

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const { hasFetchedInitialPostDrafts, draftById } = storeToRefs(surfaceDraftsStore)

  async function openDraft(): Promise<void> {
    const draft = draftById.value[draftId]
    if (draft == null) return
    surfaceDraftsStore.startEditingDraft(draft.cid)
    await expandInModal(draft.cid)
  }

  openWhenReady(hasFetchedInitialPostDrafts, openDraft)
}

export { useSurfaceUrlShortcuts }
